/**
 * Rotate Behavior
 *
 * Handles node rotation:
 * - Shows rotation cursor when hovering outside corners (donut zone)
 * - Drag to rotate around node center
 * - Shift+drag for 15-degree angle snapping
 * - R key activates rotation tool (Shift+R for locked mode)
 * - Alt key temporarily enables rotation mode
 * - Escape to cancel
 */

import type { RotateBehaviorContext } from './types';
import type { Point, EditorNode, ResizeHandlePosition } from '../types';
import { getRotatedResizeHandlePosition, RESIZE_HANDLE_POSITIONS } from '../types';
import * as viewState from '../view-state';

/** Corner positions for rotation zones */
const CORNER_POSITIONS: ResizeHandlePosition[] = ['nw', 'ne', 'sw', 'se'];

/** Inner radius of rotation zone (just outside resize handles) in screen pixels */
const ROTATION_ZONE_INNER_RADIUS = 24;

/** Outer radius of rotation zone in screen pixels */
const ROTATION_ZONE_OUTER_RADIUS = 48;

/** When rotate tool is active, extend rotation zone to include resize handles */
const ROTATION_ZONE_INNER_RADIUS_TOOL_ACTIVE = 0;

/** Angle snap increment in radians (15 degrees) */
const SNAP_ANGLE = Math.PI / 12;

/**
 * Calculate angle from center to point in radians.
 */
function angleFromCenter(center: Point, point: Point): number {
	return Math.atan2(point.y - center.y, point.x - center.x);
}

/**
 * Snap angle to nearest increment.
 */
function snapAngle(angle: number, snap: number): number {
	return Math.round(angle / snap) * snap;
}

/**
 * Get the center of a node (for rotation pivot).
 * Node uses top-left position, so center is position + size/2.
 */
function getNodeCenter(node: EditorNode): Point {
	return {
		x: node.position.x + node.size.width / 2,
		y: node.position.y + node.size.height / 2,
	};
}

/**
 * Hit test for rotation zones around a node's corners.
 * Returns true if point is in the "donut" zone outside corners.
 *
 * The rotation zone is a donut shape around each corner:
 * - Inner radius: just outside the resize handle hit zone
 * - Outer radius: extends further out for easy activation
 */
function hitTestRotationZone(
	node: EditorNode,
	point: Point,
	innerRadius: number,
	outerRadius: number
): boolean {
	// Only check corner positions (use rotated positions for rotated nodes)
	for (const position of CORNER_POSITIONS) {
		const cornerPoint = getRotatedResizeHandlePosition(node, position);
		const dx = point.x - cornerPoint.x;
		const dy = point.y - cornerPoint.y;
		const distance = Math.sqrt(dx * dx + dy * dy);

		// Check if point is in the donut zone (between inner and outer radius)
		if (distance > innerRadius && distance <= outerRadius) {
			return true;
		}
	}

	return false;
}

/**
 * Register rotate behavior handlers.
 * Returns cleanup function to unregister all handlers.
 */
export function registerRotateBehavior(ctx: RotateBehaviorContext): () => void {
	const {
		input,
		getViewState,
		updateViewState,
		getNodes,
		screenToWorld,
		rotateNode,
		getNodeRotation,
		isRotateToolActive,
		onRotationComplete,
		getZoom,
		log,
	} = ctx;
	const unsubscribers: (() => void)[] = [];

	/**
	 * Get the effective radius in world units.
	 * Scales by 1/zoom so the zone matches the visual size.
	 */
	function getEffectiveRadius(screenRadius: number): number {
		const zoom = getZoom?.() ?? 1;
		return screenRadius / zoom;
	}

	/**
	 * Get node with actual rendered size (for consistency with resize behavior).
	 */
	function getSelectedNode(): EditorNode | undefined {
		const state = getViewState();
		if (state.selection.size !== 1) return undefined;

		const selectedNodeId = [...state.selection][0];
		return getNodes().find((n) => n.id === selectedNodeId);
	}

	// =========================================================================
	// Rotation Zone Hover Detection
	// =========================================================================

	unsubscribers.push(
		input.on('mousemove', { mode: '*' }, (inputCtx) => {
			const state = getViewState();
			const point = screenToWorld(inputCtx.position!.x, inputCtx.position!.y);

			// Only show rotation cursor when exactly one node is selected
			if (state.selection.size !== 1) {
				if (state.hovered.rotationZone) {
					updateViewState(viewState.setHoveredRotationZone(state, false));
				}
				return;
			}

			// Don't update hover during other operations (except rotating)
			if (state.drag || state.connecting || state.boxSelect || state.resize) {
				return;
			}

			// Skip if currently rotating (hover stays active)
			if (state.rotate) {
				return;
			}

			// Rotation zone only active when rotate tool is active or Alt is held
			let rotateToolActive: boolean;
			try {
				rotateToolActive = isRotateToolActive();
			} catch (e) {
				return;
			}

			const altHeld = inputCtx.modifiers.alt;
			if (!rotateToolActive && !altHeld) {
				if (state.hovered.rotationZone) {
					updateViewState(viewState.setHoveredRotationZone(state, false));
				}
				return;
			}

			const node = getSelectedNode();
			if (!node) {
				log?.('rotate-hover', 'no selected node found in getNodes()');
				if (state.hovered.rotationZone) {
					updateViewState(viewState.setHoveredRotationZone(state, false));
				}
				return;
			}

			// Hit test rotation zone
			// When rotate tool is active, extend zone to include resize handles (inner radius = 0)
			const effectiveInnerRadius = rotateToolActive
				? ROTATION_ZONE_INNER_RADIUS_TOOL_ACTIVE
				: ROTATION_ZONE_INNER_RADIUS;
			const innerRadius = getEffectiveRadius(effectiveInnerRadius);
			const outerRadius = getEffectiveRadius(ROTATION_ZONE_OUTER_RADIUS);
			const inZone = hitTestRotationZone(node, point, innerRadius, outerRadius);

			if (inZone !== state.hovered.rotationZone) {
				log?.('rotate-hover', inZone ? 'entered rotation zone' : 'left rotation zone');
				updateViewState(viewState.setHoveredRotationZone(state, inZone));
			}
		})
	);

	// =========================================================================
	// Start Rotation (mousedown in rotation zone with rotate tool)
	// =========================================================================

	unsubscribers.push(
		input.on('mousedown', { mode: 'tool' }, (inputCtx) => {
			// Only start rotation if rotate tool is active
			const toolActive = isRotateToolActive();
			if (!toolActive) {
				return;
			}

			// Ignore middle mouse button
			if (inputCtx.button === 1) return;

			const state = getViewState();

			// Only rotate when hovering rotation zone
			if (!state.hovered.rotationZone) {
				return;
			}

			const node = getSelectedNode();
			if (!node) {
				return;
			}

			const point = screenToWorld(inputCtx.position!.x, inputCtx.position!.y);
			const center = getNodeCenter(node);
			const startAngle = angleFromCenter(center, point);
			const originalRotation = getNodeRotation?.(node.id) ?? node.rotation ?? 0;

			// Start rotation
			updateViewState(viewState.startRotation(state, node.id, center, startAngle, originalRotation));
			input.pushMode('rotate');
			log?.('rotate-start', `${node.id}`);
			inputCtx.stopPropagation();
		})
	);

	// =========================================================================
	// Start Rotation (mousedown with Alt modifier in default mode)
	// =========================================================================

	unsubscribers.push(
		input.on('mousedown', { mode: 'default' }, (inputCtx) => {
			// Only start rotation if Alt is held and in rotation zone
			if (!inputCtx.modifiers.alt) return;

			// Ignore middle mouse button
			if (inputCtx.button === 1) return;

			const state = getViewState();

			// Only rotate when hovering rotation zone
			if (!state.hovered.rotationZone) return;

			const node = getSelectedNode();
			if (!node) return;

			const point = screenToWorld(inputCtx.position!.x, inputCtx.position!.y);
			const center = getNodeCenter(node);
			const startAngle = angleFromCenter(center, point);
			const originalRotation = getNodeRotation?.(node.id) ?? node.rotation ?? 0;

			// Start rotation
			updateViewState(viewState.startRotation(state, node.id, center, startAngle, originalRotation));
			input.pushMode('rotate');
			log?.('rotate-start', `${node.id} (alt-modifier)`);
			inputCtx.stopPropagation();
		})
	);

	// =========================================================================
	// Rotation Move
	// =========================================================================

	unsubscribers.push(
		input.on('mousemove', { mode: 'rotate' }, (inputCtx) => {
			const state = getViewState();
			if (!state.rotate) {
				return;
			}

			const point = screenToWorld(inputCtx.position!.x, inputCtx.position!.y);
			let currentAngle = angleFromCenter(state.rotate.centerPoint, point);

			// Apply 15-degree snapping when Shift is held
			if (inputCtx.modifiers.shift) {
				// Snap the final rotation (original + delta), not just the current angle
				const delta = currentAngle - state.rotate.startAngle;
				const targetRotation = state.rotate.originalRotation + delta;
				const snappedRotation = snapAngle(targetRotation, SNAP_ANGLE);
				// Calculate what currentAngle should be to achieve the snapped rotation
				currentAngle = state.rotate.startAngle + (snappedRotation - state.rotate.originalRotation);
			}

			updateViewState(viewState.updateRotation(state, currentAngle));
		})
	);

	// =========================================================================
	// Rotation End
	// =========================================================================

	unsubscribers.push(
		input.on('mouseup', { mode: 'rotate' }, (inputCtx) => {
			const state = getViewState();

			if (state.rotate) {
				const { nodeId } = state.rotate;
				const finalRotation = viewState.getFinalRotation(state);

				if (finalRotation !== null) {
					// Apply the rotation
					rotateNode(nodeId, finalRotation);
					log?.(
						'rotate-end',
						`${nodeId} -> ${((finalRotation * 180) / Math.PI).toFixed(1)}deg`
					);
				}

				// Notify that tool was used (for auto-exit)
				onRotationComplete?.();
			}

			// Finish rotation state
			updateViewState(viewState.finishRotation(getViewState()));
			input.popMode('rotate');
			// Prevent event from reaching other behaviors
			inputCtx.stopPropagation();
		})
	);

	// =========================================================================
	// Cancel Rotation
	// =========================================================================

	unsubscribers.push(
		input.on(
			'keydown',
			'escape',
			{
				mode: 'rotate',
				command: { id: 'rotate.cancel', name: 'Cancel Rotation' },
			},
			() => {
				log?.('rotate-cancel', '');
				updateViewState(viewState.cancelRotation(getViewState()));
				input.popMode('rotate');
			}
		)
	);

	// =========================================================================
	// Cleanup
	// =========================================================================

	return () => {
		for (const unsub of unsubscribers) {
			unsub();
		}
	};
}
